/**
 * pickLang — resolve a translatable field for the currently active language.
 *
 * Data shape written by the CMS translation UI:
 *   {
 *     title: "PT title (source)",
 *     translations: {
 *       en: { title: "EN title", ... },
 *       es: { title: "ES title", ... },
 *     }
 *   }
 *
 * @param {object} item   the CMS document (case / insight / job / etc.)
 * @param {string} lang   short lang code from useLang(): "pt" | "en" | "es"
 * @param {string} field  field key to resolve (e.g. "title", "situation", "responsibilities")
 * @returns the translated value if present; otherwise the PT source value.
 *
 * The site's default language is PT — any missing translation transparently
 * falls back to the PT field so nothing ever renders blank.
 */
export function pickLang(item, lang, field) {
  if (!item) return undefined;
  if (lang && lang !== "pt") {
    const t = item.translations && item.translations[lang];
    if (t && t[field] !== undefined && t[field] !== null && t[field] !== "") {
      return t[field];
    }
  }
  return item[field];
}

/**
 * pickLangObj — like pickLang but returns an object that overlays translated
 * fields on top of the PT source. Useful when a component reads many fields
 * from the same item without threading `lang` through every access.
 */
export function pickLangObj(item, lang, fields = null) {
  if (!item) return item;
  if (!lang || lang === "pt") return item;
  const overlay = (item.translations && item.translations[lang]) || {};
  const out = { ...item };
  const keys = fields || Object.keys(overlay);
  keys.forEach((k) => {
    const v = overlay[k];
    if (v !== undefined && v !== null && v !== "") out[k] = v;
  });
  return out;
}

/** Field lists per collection — used by the translate button + fallback overlay. */
export const TRANSLATABLE_FIELDS = {
  case: [
    "title", "subtitle", "concept", "cover_alt",
    "situation", "solution", "strategy", "deliveries", "results",
  ],
  insight: ["title", "summary", "body"],
  job: [
    "title", "location", "department", "employment_type",
    "overview", "responsibilities", "requirements", "extras",
  ],
};
